/**
 * Utility for generating automatic invoice numbers
 * Format: Prefix + Year/NextYear + Month + Serial Number
 * Example: FC2025/26-Sep-001 (Friends Carrier, 2025/26, September, 001)
 */

/**
 * Get short month name from month number
 * @param {number} month - Month number (1-12)
 * @returns {string} - Short month name (Jan, Feb, Mar, etc.)
 */
export function getShortMonthName(month) {
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  return monthNames[month - 1] || 'Jan';
}

/**
 * Generate prefix from company name
 * Takes the first letter of each word in company name
 * @param {string} companyName - The company name
 * @returns {string} - The generated prefix (uppercase)
 */
export function generatePrefixFromCompanyName(companyName) {
  if (!companyName || typeof companyName !== 'string') {
    return 'INV'; // Default prefix
  }
  
  // Split by spaces and take first letter of each word
  const words = companyName.trim().split(/\s+/);
  const prefix = words
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 4); // Limit to max 4 characters
  
  return prefix || 'INV'; // Fallback to 'INV' if no valid characters
}

/**
 * Check if an invoice number follows the auto-generated format for a specific company
 * @param {string} invoiceNumber - The invoice number to check
 * @param {string} prefix - The company prefix to match
 * @returns {boolean} - True if it's an auto-generated format for this company
 */
export function isAutoGeneratedFormat(invoiceNumber, prefix) {
  if (!invoiceNumber || !prefix) return false;
  
  // Pattern: PREFIX + YEAR + / + NEXTYEAR + - + MONTHNAME + - + SERIALNUMBER
  // Example: TC2025/26-Sep-001
  const autoGenPattern = new RegExp(`^${prefix}\\d{4}/\\d{2}-[A-Z][a-z]{2}-\\d{3}$`);
  return autoGenPattern.test(invoiceNumber);
}

/**
 * Generate the next serial number (continuous across all months, only considering auto-generated invoices)
 * This ensures custom invoice numbers don't affect the auto-generated sequence
 * 
 * Logic: Auto-generated invoices should form a continuous sequence (1, 2, 3, ...).
 * If we find a large gap in the sequence (like 10 to 100), we assume the higher numbers are custom.
 * 
 * Example scenario:
 * - Auto-generated: TC2025/26-Sep-001, TC2025/26-Sep-002, ..., TC2025/26-Sep-010
 * - User enters custom: TC2025/26-Sep-100
 * - Next auto-generated should be: TC2025/26-Sep-011 (not TC2025/26-Sep-101)
 * 
 * @param {Array} existingInvoices - Array of existing invoices
 * @param {string} prefix - The company prefix
 * @returns {string} - The next serial number (3-digit, zero-padded)
 */
export function getNextSerialNumber(existingInvoices, prefix) {
  if (!Array.isArray(existingInvoices)) {
    return '001';
  }

  // Find all invoices with the correct format for this company
  const companyInvoices = existingInvoices.filter(invoice => {
    const invoiceNo = invoice.invoiceNo || '';
    return isAutoGeneratedFormat(invoiceNo, prefix);
  });

  if (companyInvoices.length === 0) {
    return '001'; // First invoice for this company
  }

  // Extract and sort serial numbers
  const serialNumbers = companyInvoices
    .map(invoice => {
      const invoiceNo = invoice.invoiceNo || '';
      // Extract the last part after the final dash (the serial number)
      const parts = invoiceNo.split('-');
      const serialPart = parts[parts.length - 1]; // Last part after final dash
      const serialNum = parseInt(serialPart, 10);
      return isNaN(serialNum) ? 0 : serialNum;
    })
    .filter(num => num > 0)
    .sort((a, b) => a - b); // Sort ascending

  if (serialNumbers.length === 0) {
    return '001';
  }

  // Find the last continuous number in the sequence
  // This assumes auto-generated numbers form a continuous sequence from 1
  let lastContinuous = 0;
  
  for (let i = 0; i < serialNumbers.length; i++) {
    const currentNum = serialNumbers[i];
    const expectedNext = lastContinuous + 1;
    
    if (currentNum === expectedNext) {
      // This number continues the sequence
      lastContinuous = currentNum;
    } else if (currentNum > expectedNext) {
      // Found a gap - likely custom numbers from this point
      // Stop here and use the last continuous number
      break;
    }
    // If currentNum < expectedNext, it's a duplicate (shouldn't happen but skip)
  }

  const nextSerial = lastContinuous + 1;
  return nextSerial.toString().padStart(3, '0'); // Zero-pad to 3 digits
}

/**
 * Generate automatic invoice number
 * @param {string} companyName - The company name
 * @param {Array} existingInvoices - Array of existing invoices
 * @param {Date} [date] - Optional date (defaults to current date)
 * @returns {string} - The generated invoice number
 */
export function generateInvoiceNumber(companyName, existingInvoices = [], date = new Date()) {
  try {
    // Generate prefix from company name
    const prefix = generatePrefixFromCompanyName(companyName);
    
    // Get year and month (4-digit year, 2-digit next year, short month name)
    const year = date.getFullYear(); // Full 4-digit year
    const nextYear = (year + 1).toString().slice(-2); // Last 2 digits of next year
    const month = date.getMonth() + 1; // 1-12
    const monthName = getShortMonthName(month); // Convert to short month name
    
    // Get next serial number (continuous across all months)
    const serial = getNextSerialNumber(existingInvoices, prefix);
    
    // Combine all parts with format: PREFIX + YEAR + / + NEXTYEAR + - + MONTHNAME + - + SERIAL
    const invoiceNumber = `${prefix}${year}/${nextYear}-${monthName}-${serial}`;
    
    return invoiceNumber;
  } catch (error) {
    console.error('Error generating invoice number:', error);
    // Fallback to timestamp-based number
    const timestamp = Date.now().toString().slice(-6);
    return `INV${timestamp}`;
  }
}

/**
 * Validate if an invoice number follows the expected auto-generated format
 * @param {string} invoiceNumber - The invoice number to validate
 * @returns {boolean} - True if valid auto-generated format
 */
export function isValidInvoiceNumberFormat(invoiceNumber) {
  if (!invoiceNumber || typeof invoiceNumber !== 'string') {
    return false;
  }
  
  // Pattern: 2-4 letters + 4 digits (year) + / + 2 digits (next year) + - + 3 letters (month) + - + 3 digits (serial)
  // Example: TC2025/26-Sep-001, ABCD2025/26-Jan-001
  const pattern = /^[A-Z]{2,4}\d{4}\/\d{2}-[A-Z][a-z]{2}-\d{3}$/;
  return pattern.test(invoiceNumber);
}

/**
 * Parse invoice number components
 * @param {string} invoiceNumber - The invoice number to parse
 * @returns {Object} - Object with prefix, year, nextYear, monthName, serial
 */
export function parseInvoiceNumber(invoiceNumber) {
  if (!isValidInvoiceNumberFormat(invoiceNumber)) {
    return null;
  }
  
  // Pattern: PREFIX + YEAR + / + NEXTYEAR + - + MONTHNAME + - + SERIAL
  // Example: FC2025/26-Sep-001
  const match = invoiceNumber.match(/^([A-Z]{2,4})(\d{4})\/(\d{2})-([A-Z][a-z]{2})-(\d{3})$/);
  
  if (!match) return null;
  
  const [, prefix, yearStr, nextYearStr, monthName, serialStr] = match;
  const year = parseInt(yearStr, 10);
  const nextYear = parseInt(`20${nextYearStr}`, 10); // Convert 26 to 2026
  const serial = parseInt(serialStr, 10);
  
  // Convert month name back to number for compatibility
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames.indexOf(monthName) + 1;
  
  return {
    prefix,
    year,
    nextYear,
    monthName,
    month,
    serial,
    fullYear: year
  };
}
